import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { SecretField, TextField } from '@/shared/ui/Field';

import { useLogIn, useResendEmailVerification } from '../api/auth';
import { announcesPasswordChange } from '../model/reset';
import { announcesEmailVerified, isEmailNotVerified } from '../model/verification';

/* Standalone, outside `AppShell` — there is no tenant to bind and no sidebar to render before one
 * exists. `onLoggedIn` rather than a bare redirect here, so `LoginScreen` decides where "in" means,
 * matching `RegisterConnectionForm`'s separation between a form's outcome and the screen's response
 * to it. */
export function LoginForm({
  onLoggedIn,
}: {
  readonly onLoggedIn: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const login = useLogIn();
  const resend = useResendEmailVerification();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetKey, setResetKey] = useState(0);
  /* Annotated `unknown` rather than left as the router's `any`: `location.state` is whatever the
   * previous screen put there, which is `null` on a cold load and anything at all after a Back
   * button, so it is untrusted input and `announcesPasswordChange` guards it. */
  const navigationState: unknown = useLocation().state;
  const passwordChanged = announcesPasswordChange(navigationState);
  const emailVerified = announcesEmailVerified(navigationState);
  /* **Derived from the live error rather than held in state**, so it cannot outlive the attempt
     that produced it. A `useState` flag set in `onError` would still be true after a *second*
     attempt failed with a wrong password, and the reader would be told their address is unverified
     when the server had just said something else — the stale-message defect CLAUDE.md records for
     the suggestions screen. */
  const unverified = login.isError && isEmailNotVerified(login.error);

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (email.trim() === '' || password === '') return;
    login.mutate(
      { email: email.trim(), password },
      {
        onSuccess: () => {
          onLoggedIn();
        },
        onError: () => {
          /* The password never survives a failed attempt in memory longer than it has to — cleared
           * so a screenshot or a crash report taken after the error does not carry it. */
          setPassword('');
          setResetKey((generation) => generation + 1);
        },
      },
    );
  };

  return (
    <>
      {/* **A heading, not a nested `Card`.** `AuthShell` already renders the card — surface, radius,
          shadow and 28px of padding — so wrapping this form in another one produced two stacked
          surfaces and **two layers of padding**, which is why the submit button came out 292px wide
          inside a 324px box instead of spanning it. Same double-padding class as `.lq-main` over
          `.lq-content`, one component deeper.

          The reference's auth card is a single container with a 16px/600 heading inside it, which is
          what this now is. */}
      <h1 className="lq-auth__heading">{t('auth.login.title')}</h1>

      {passwordChanged && (
        /* **Two sentences, and the second is the one nobody expects.** The password is changed, and
           sessions already open elsewhere are *not* signed out: the JWT is stateless with a one-hour
           TTL and there is no denylist, so a token issued before the change keeps working until it
           expires. `UserDrawer` states the same fact about disabling an account, for the same reason
           — a reader who has just changed a password because they think somebody else has it needs
           to know that changing it did not evict them. Saying nothing would be a claim by silence,
           and it is the claim a reader is most likely to make on their own. */
        <Alert tone="notice" title={t('auth.login.passwordChangedTitle')}>
          {t('auth.login.passwordChangedBody')}
        </Alert>
      )}

      {emailVerified && (
        /* **The safe success message ADR-014 asks for**, and it carries no address and no token —
           `/login` is a public URL a reader may bookmark or share, and the router state that put
           this here says only that *a* verification succeeded. Naming the account would turn a
           forwarded link into a disclosure. */
        <Alert tone="ok" title={t('auth.login.emailVerifiedTitle')}>
          {t('auth.login.emailVerifiedBody')}
        </Alert>
      )}

      <form onSubmit={onSubmit}>
        <TextField
          label={t('auth.field.email')}
          type="text"
          value={email}
          onChange={setEmail}
          required
        />
        <SecretField
          label={t('auth.field.password')}
          onChange={setPassword}
          required
          resetKey={resetKey}
        />
        {login.isError && !unverified && (
          <Alert tone="error" title={t('auth.login.failedTitle')}>
            {login.error.message}
          </Alert>
        )}
        {unverified && (
          /* **Its own card, its own tone, and its own action.** This is the one login refusal the
             server is willing to be specific about, and it is specific precisely because it is
             reachable only with the correct password — so the person reading it owns the account,
             and the useful response is a button rather than a sentence. Rendering it as an `error`
             beside "the email or password is incorrect" would tell them to check their typing,
             which is the one thing that cannot help.

             The copy is this catalogue's rather than the server's `detail`, unlike the generic
             refusal above: that one is deliberately opaque and a screen improving on it would undo
             the withholding, while this one is a known, actionable state that should be
             translatable. */
          <Alert tone="warning" title={t('auth.login.unverifiedTitle')}>
            <p>{t('auth.login.unverifiedBody')}</p>
            {resend.isSuccess && (
              <p>
                {resend.data.delivered
                  ? t('auth.login.resendSent', {
                      minutes: resend.data.expires_in_minutes,
                    })
                  : t('auth.login.resendUndeliverable')}
              </p>
            )}
            {resend.isError && <p>{t('auth.login.resendFailed')}</p>}
            {!resend.isSuccess && (
              <div style={{ marginTop: 'var(--space-3)' }}>
                <Button
                  tone="secondary"
                  busy={resend.isPending}
                  onClick={() => {
                    /* The trimmed address from the field, which is the value that was just
                       refused — never `resend.variables`, which is undefined on a first press. */
                    resend.mutate(email.trim());
                  }}
                >
                  {t('auth.login.resendSubmit')}
                </Button>
              </div>
            )}
          </Alert>
        )}
        <Button type="submit" tone="primary" size="block" busy={login.isPending}>
          {t('auth.login.submit')}
        </Button>
      </form>
      {/* **Below the button rather than beside the password label**, which is where the reference
          puts it and where a reader looks after a refusal rather than before one. Its own line
          above the sign-up prompt, because "I cannot get in" and "I have no account" are different
          problems and a single line carrying both reads as one sentence with two links in it. */}
      <p className="lq-dim" style={{ marginTop: 'var(--space-4)' }}>
        <Link to={SCREEN_PATHS.forgotPassword}>{t('auth.login.forgotLink')}</Link>
      </p>
      <p className="lq-dim" style={{ marginTop: 'var(--space-2)' }}>
        {t('auth.login.noAccount')}{' '}
        <Link to={SCREEN_PATHS.signup}>{t('auth.login.signupLink')}</Link>
      </p>
    </>
  );
}
